import { multiply, add, subtract } from './decimal';
import { isOrderUnitMultiple } from './quantity';
/**
 * 수량 검증 함수
 * @param quantity - 수량
 * @param orderUnit - 주문 단위 수량 (선택사항)
 * @param minOrderQuantity - 최소 주문 수량 (선택사항)
 * @param maxOrderQuantity - 최대 주문 수량 (선택사항)
 * @returns 검증 결과 객체
 */
export const validateQuantity = (quantity, orderUnit, minOrderQuantity, maxOrderQuantity) => {
    if (!quantity) {
        return {
            isValid: false,
            errorMessage: '수량을 입력하세요',
        };
    }
    // orderUnit이 제공된 경우 배수 검증
    if (orderUnit !== undefined && orderUnit > 0) {
        if (!isOrderUnitMultiple(quantity, orderUnit)) {
            return {
                isValid: false,
                errorMessage: `수량은 ${orderUnit}의 배수여야 합니다.`,
            };
        }
    }
    // minOrderQuantity가 제공된 경우 최소값 검증
    if (minOrderQuantity !== undefined) {
        if (quantity < minOrderQuantity) {
            return {
                isValid: false,
                errorMessage: `수량은 ${minOrderQuantity}보다 크거나 같아야 합니다.`,
            };
        }
    }
    else {
        // 기본 최소값 검증 (하위 호환성)
        if (quantity < 0.01) {
            return {
                isValid: false,
                errorMessage: '수량은 0.01보다 크거나 같아야 합니다.',
            };
        }
    }
    // maxOrderQuantity가 제공된 경우 최대값 검증
    if (maxOrderQuantity !== undefined) {
        if (quantity > maxOrderQuantity) {
            return {
                isValid: false,
                errorMessage: `수량은 ${maxOrderQuantity}보다 작거나 같아야 합니다.`,
            };
        }
    }
    else {
        // 기본 최대값 검증 (하위 호환성)
        if (quantity > 100) {
            return {
                isValid: false,
                errorMessage: '수량은 100보다 작거나 같아야 합니다.',
            };
        }
    }
    return { isValid: true };
};
/**
 * 지정가 진입 가격 검증 함수
 * @param entryPrice - 진입 가격
 * @param orderType - 주문 유형
 * @param orderSide - 주문 방향 (BUY/SELL)
 * @param buyPrice - 매수 현재가
 * @param sellPrice - 매도 현재가
 * @returns 검증 결과 객체
 */
export const validateEntryPrice = (entryPrice, orderType, orderSide, buyPrice, sellPrice) => {
    // 지정가가 아닌 경우 검증하지 않음
    if (orderType !== 'LIMIT') {
        return { isValid: true };
    }
    // 매수일 때: 진입 가격 <= 매수 현재가
    if (orderSide === 'BUY' || orderSide === 'BUY_TO_COVER') {
        if (entryPrice > buyPrice) {
            return {
                isValid: false,
                // errorMessage: `진입 가격은 매수 현재가(${buyPrice.toFixed(5)})보다 작거나 같아야 합니다.`,
                errorMessage: '유효한 가격을 입력하세요',
            };
        }
    }
    // 매도일 때: 진입 가격 >= 매도 현재가
    if (orderSide === 'SELL' || orderSide === 'SELL_TO_CLOSE') {
        if (entryPrice < sellPrice) {
            return {
                isValid: false,
                errorMessage: '유효한 가격을 입력하세요',
                // errorMessage: `진입 가격은 매도 현재가(${sellPrice.toFixed(5)})보다 크거나 같아야 합니다.`,
            };
        }
    }
    return { isValid: true };
};
/**
 * 배리어 가격 검증 함수
 * @param barrier - 배리어 가격
 * @param orderType - 주문 유형
 * @param orderSide - 주문 방향 (BUY/SELL)
 * @param buyPrice - 매수 현재가
 * @param sellPrice - 매도 현재가
 * @returns 검증 결과 객체
 */
export const validateBarrier = (barrier, orderType, orderSide, buyPrice, sellPrice) => {
    // 조건시장가나 조건지정가가 아닌 경우 검증하지 않음
    if (orderType !== 'STOP' && orderType !== 'LIMIT_STOP') {
        return { isValid: true };
    }
    // 매수일 때: 배리어 가격 >= 매수 현재가
    if (orderSide === 'BUY' || orderSide === 'BUY_TO_COVER') {
        if (barrier < buyPrice) {
            return {
                isValid: false,
                // errorMessage: `배리어 가격은 매수 현재가(${buyPrice.toFixed(5)})보다 크거나 같아야 합니다.`,
                errorMessage: '유효한 가격을 입력하세요',
            };
        }
    }
    // 매도일 때: 배리어 가격 <= 매도 현재가
    if (orderSide === 'SELL' || orderSide === 'SELL_TO_CLOSE') {
        if (barrier > sellPrice) {
            return {
                isValid: false,
                // errorMessage: `배리어 가격은 매도 현재가(${sellPrice.toFixed(5)})보다 작거나 같아야 합니다.`,
                errorMessage: '유효한 가격을 입력하세요',
            };
        }
    }
    return { isValid: true };
};
/**
 * 조건지정가 진입가격 Pip 검증 함수
 * @param entryPricePip - 진입가격 Pip
 * @param orderType - 주문 유형
 * @param orderSide - 주문 방향 (BUY/SELL)
 * @param barrier - 배리어 가격
 * @param pipUnit - Pip 단위
 * @returns 검증 결과 객체
 */
export const validateEntryPricePip = (entryPricePip, orderType, orderSide, barrier, pipUnit) => {
    // 조건지정가가 아닌 경우 검증하지 않음
    if (orderType !== 'LIMIT_STOP') {
        return { isValid: true };
    }
    // 진입가격 Pip은 0보다 커야 함
    if (entryPricePip <= 0) {
        return {
            isValid: false,
            errorMessage: '진입가격 Pip은 0보다 커야 합니다.',
        };
    }
    // 진입가격 계산: 매수 = 배리어 + (Pip × 단위), 매도 = 배리어 - (Pip × 단위)
    const pipOffset = multiply(entryPricePip, pipUnit);
    const calculatedEntryPrice = orderSide === 'BUY' || orderSide === 'BUY_TO_COVER'
        ? add(barrier, pipOffset)
        : subtract(barrier, pipOffset);
    // 매수일 때: 진입가격 >= 배리어 가격
    if (orderSide === 'BUY' || orderSide === 'BUY_TO_COVER') {
        if (calculatedEntryPrice < barrier) {
            return {
                isValid: false,
                errorMessage: `진입가격(${calculatedEntryPrice.toFixed(5)})은 배리어 가격(${barrier.toFixed(5)})보다 크거나 같아야 합니다.`,
            };
        }
    }
    // 매도일 때: 진입가격 <= 배리어 가격
    if (orderSide === 'SELL' || orderSide === 'SELL_TO_CLOSE') {
        if (calculatedEntryPrice > barrier) {
            return {
                isValid: false,
                errorMessage: `진입가격(${calculatedEntryPrice.toFixed(5)})은 배리어 가격(${barrier.toFixed(5)})보다 작거나 같아야 합니다.`,
            };
        }
    }
    return { isValid: true };
};
/**
 * Stop Loss 검증 함수
 * @param stopLossEnabled - Stop Loss 활성화 여부
 * @param stopLossPip - Stop Loss Pip 값
 * @param stopLossPrice - Stop Loss 가격
 * @param entryPrice - 진입가격
 * @param pipUnit - Pip 단위
 * @param adjustmentSource - 현재 포커스된 입력 필드 (pip 또는 price)
 * @returns 검증 결과 객체
 */
export const validateStopLoss = (stopLossEnabled, stopLossPip, stopLossPrice, entryPrice, pipUnit, orderSide, adjustmentSource) => {
    // Stop Loss가 비활성화되어 있으면 검증하지 않음
    if (!stopLossEnabled) {
        return { isValid: true };
    }
    // Pip이 포커스일 때: 0.1 pip보다 작아야 함
    if (adjustmentSource === 'pip') {
        if (stopLossPip > -0.1) {
            return {
                isValid: false,
                errorMessage: orderSide === 'BUY' || orderSide === 'SELL_TO_CLOSE'
                    ? 'Stop Loss Pip은 진입가격보다 0.1 pip만큼 낮아야 합니다.'
                    : 'Stop Loss Pip은 진입가격보다 0.1 pip만큼 높아야 합니다.',
            };
        }
    }
    // 가격이 포커스일 때: 진입가격보다 pipUnit * 0.1보다 작아야 함
    if (adjustmentSource === 'price') {
        const minPriceDiff = multiply(pipUnit, 0.1);
        const maxStopLossPrice = subtract(entryPrice, minPriceDiff);
        if (orderSide === 'BUY' || orderSide === 'SELL_TO_CLOSE') {
            if (stopLossPrice > maxStopLossPrice) {
                return {
                    isValid: false,
                    errorMessage: `Stop Loss 가격은 진입가격보다 ${minPriceDiff.toFixed(5)} 만큼 낮아야 합니다.`,
                };
            }
        }
        else if (orderSide === 'SELL' || orderSide === 'BUY_TO_COVER') {
            if (stopLossPrice < maxStopLossPrice) {
                return {
                    isValid: false,
                    errorMessage: `Stop Loss 가격은 진입가격보다 ${minPriceDiff.toFixed(5)} 만큼 높아야 합니다.`,
                };
            }
        }
    }
    return { isValid: true };
};
/**
 * Take Profit 검증 함수
 * @param takeProfitEnabled - Take Profit 활성화 여부
 * @param takeProfitPip - Take Profit Pip 값
 * @param takeProfitPrice - Take Profit 가격
 * @param entryPrice - 진입가격
 * @param pipUnit - Pip 단위
 * @param adjustmentSource - 현재 포커스된 입력 필드 (pip 또는 price)
 * @returns 검증 결과 객체
 */
export const validateTakeProfit = (takeProfitEnabled, takeProfitPip, takeProfitPrice, entryPrice, pipUnit, orderSide, adjustmentSource) => {
    // Take Profit이 비활성화되어 있으면 검증하지 않음
    if (!takeProfitEnabled) {
        return { isValid: true };
    }
    // Pip이 포커스일 때: 0.1 pip보다 커야 함
    if (adjustmentSource === 'pip') {
        if (takeProfitPip < 0.1) {
            return {
                isValid: false,
                errorMessage: orderSide === 'BUY' || orderSide === 'SELL_TO_CLOSE'
                    ? 'Take Profit Pip은 진입가격보다 0.1 pip만큼 높아야 합니다.'
                    : 'Take Profit Pip은 진입가격보다 0.1 pip만큼 낮아야 합니다.',
            };
        }
    }
    // 가격이 포커스일 때: 진입가격보다 pipUnit * 0.1보다 커야 함
    if (adjustmentSource === 'price') {
        const minPriceDiff = multiply(pipUnit, 0.1);
        const minTakeProfitPrice = add(entryPrice, minPriceDiff);
        if (orderSide === 'BUY' || orderSide === 'SELL_TO_CLOSE') {
            if (takeProfitPrice < minTakeProfitPrice) {
                return {
                    isValid: false,
                    errorMessage: `Take Profit 가격은 진입가격보다 ${minPriceDiff.toFixed(5)} 만큼 높아야 합니다.`,
                };
            }
        }
        else if (orderSide === 'SELL' || orderSide === 'BUY_TO_COVER') {
            if (takeProfitPrice > minTakeProfitPrice) {
                return {
                    isValid: false,
                    errorMessage: `Take Profit 가격은 진입가격보다 ${minPriceDiff.toFixed(5)} 만큼 낮아야 합니다.`,
                };
            }
        }
    }
    return { isValid: true };
};
//# sourceMappingURL=order-validation.js.map